import {
	type OperatorValue,
	OPERATOR_EQUALS,
	OPERATOR_IN,
	OPERATOR_IS,
	OPERATOR_IS_NOT,
	OPERATOR_NOT_EQUALS,
	OPERATOR_NOT_IN,
	OPERATOR_WAS,
	OPERATOR_WAS_IN,
	OPERATOR_WAS_NOT,
	OPERATOR_WAS_NOT_IN,
	type Operator,
	OPERATOR_CHANGED,
	OPERATOR_LIKE,
	OPERATOR_NOT_LIKE,
	OPERATOR_GT,
	OPERATOR_GT_EQUALS,
	OPERATOR_LT,
	OPERATOR_LT_EQUALS,
} from '@atlaskit/jql-ast';
import type { OperatorOption } from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import messages from './messages.tsx';

const getOperatorLabels = ({
	operatorValue,
	fieldDisplayName,
	formatMessage,
}: {
	operatorValue: OperatorValue;
	fieldDisplayName: string;
	formatMessage: FormatMessage;
}) => {
	let messageKey:
		| 'operatorEquals'
		| 'operatorNotEquals'
		| 'operatorWas'
		| 'operatorWasNot'
		| 'operatorChanged'
		| 'operatorLike'
		| 'operatorNotLike'
		| 'operatorGt'
		| 'operatorGtEquals'
		| 'operatorLt'
		| 'operatorLtEquals';
	let resolvedOperatorValue: OperatorValue;
	switch (operatorValue) {
		case OPERATOR_EQUALS:
		case OPERATOR_IS:
		case OPERATOR_IN:
			messageKey = 'operatorEquals';
			resolvedOperatorValue = OPERATOR_EQUALS;
			break;
		case OPERATOR_NOT_EQUALS:
		case OPERATOR_IS_NOT:
		case OPERATOR_NOT_IN:
			messageKey = 'operatorNotEquals';
			resolvedOperatorValue = OPERATOR_NOT_EQUALS;
			break;
		case OPERATOR_WAS:
		case OPERATOR_WAS_IN:
			messageKey = 'operatorWas';
			resolvedOperatorValue = OPERATOR_WAS;
			break;
		case OPERATOR_WAS_NOT:
		case OPERATOR_WAS_NOT_IN:
			messageKey = 'operatorWasNot';
			resolvedOperatorValue = OPERATOR_WAS_NOT;
			break;
		case OPERATOR_CHANGED:
			messageKey = 'operatorChanged';
			resolvedOperatorValue = OPERATOR_CHANGED;
			break;
		case OPERATOR_LIKE:
			messageKey = 'operatorLike';
			resolvedOperatorValue = OPERATOR_LIKE;
			break;
		case OPERATOR_NOT_LIKE:
			messageKey = 'operatorNotLike';
			resolvedOperatorValue = OPERATOR_NOT_LIKE;
			break;
		case OPERATOR_GT:
			messageKey = 'operatorGt';
			resolvedOperatorValue = OPERATOR_GT;
			break;
		case OPERATOR_GT_EQUALS:
			messageKey = 'operatorGtEquals';
			resolvedOperatorValue = OPERATOR_GT_EQUALS;
			break;
		case OPERATOR_LT:
			messageKey = 'operatorLt';
			resolvedOperatorValue = OPERATOR_LT;
			break;
		case OPERATOR_LT_EQUALS:
			messageKey = 'operatorLtEquals';
			resolvedOperatorValue = OPERATOR_LT_EQUALS;
			break;
		default:
			throw new Error(`Operator value: ${operatorValue} not supported`);
	}

	return {
		label: formatMessage(messages[messageKey], {
			fieldDisplayName,
			operator: resolvedOperatorValue,
		}),
		shortLabel: formatMessage(messages[`${messageKey}Short`], {
			operator: resolvedOperatorValue,
		}),
		ariaLabel: formatMessage(messages[`${messageKey}AriaLabel`], {
			fieldDisplayName,
		}),
		valueLabel: resolvedOperatorValue,
	};
};

export const createOperatorOption = ({
	fieldDisplayName,
	operator,
	formatMessage,
}: {
	fieldDisplayName: string;
	operator: Operator;
	formatMessage: FormatMessage;
}): OperatorOption => ({
	...operator,
	...getOperatorLabels({ operatorValue: operator.value, fieldDisplayName, formatMessage }),
});
